import { useQuery, UseQueryResult, UseQueryOptions, QueryKey } from "@tanstack/react-query";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";
import { Variables } from "graphql-request";
import { v4 as uuidv4 } from "uuid";

import { useSafeAuth0GetAccessToken } from "../../../hooks/useSafeAuth0GetAccessToken";
import { executeGraphQLRequest } from "./executeGraphQLRequest";

export type GraphQLQueryParams<TQueryResult, TVariables, TData, TError> = {
  url: string;
  queryKey: QueryKey;
  document: TypedDocumentNode<TQueryResult, TVariables> | string;
  variables?: TVariables;
  options?: Omit<UseQueryOptions<TQueryResult, TError, TData>, "queryKey" | "queryFn">;
  requestHeaders?: Record<string, string>;
  /**
   * Extra `fetch` options (e.g. `credentials`, `mode`, `cache`, `signal`) spread
   * onto the underlying `GraphQLClient`. `method`, `body`, and `headers` are
   * managed by this util and therefore excluded.
   */
  requestInit?: Omit<RequestInit, "method" | "body" | "headers">;
  getAccessToken?: () => Promise<string>;
};

// Returns query options
export function getGraphQLQuery<TQueryResult, TVariables extends Variables, TData = TQueryResult, TError = Error>({
  url,
  queryKey,
  document,
  variables,
  options = {},
  requestHeaders = {},
  requestInit,
  getAccessToken,
}: GraphQLQueryParams<TQueryResult, TVariables, TData, TError>): UseQueryOptions<TQueryResult, TError, TData> {
  const queryFn = async () => {
    let authToken: string | undefined;
    if (getAccessToken) authToken = await getAccessToken();
    const headers = {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      "X-REQUEST-ID": uuidv4(),
      ...requestHeaders,
    };
    return executeGraphQLRequest<TQueryResult, TVariables>({
      url,
      document,
      variables,
      requestHeaders: headers,
      requestInit,
    });
  };

  return { queryKey, queryFn, ...options };
}

export function useGraphQLQuery<TQueryResult, TVariables extends Variables, TData = TQueryResult, TError = Error>(
  params: Omit<GraphQLQueryParams<TQueryResult, TVariables, TData, TError>, "getAccessToken">,
): UseQueryResult<TData, TError> {
  const { getAccessTokenSilently } = useSafeAuth0GetAccessToken();
  const query = getGraphQLQuery<TQueryResult, TVariables, TData, TError>({
    ...params,
    getAccessToken: getAccessTokenSilently,
  });
  return useQuery<TQueryResult, TError, TData>(query);
}
